// Copy for the FREE summary report (SummaryReport.js). Kept apart from i18n.js on
// purpose: i18n.js serves the paid five-tab report, whose wording must not move
// under it. German is authoritative — it is copied from the sales-funnel
// proposal §B2 word for word; English mirrors it for non-German businesses.
//
// Rules every string here obeys: every number comes from the business's own
// data; nothing is estimated; no urgency or scarcity that is not real; legal
// points are "Hinweis, keine Rechtsberatung".
//
// One narrow, explicitly-marked exception (2026-09-27, owner request): the
// hero headline's maps-rank/heatmap variants below also cite a *general*,
// independently-published local-search click-behaviour statistic (never a
// number about THIS business) to make the gap concrete instead of abstract.
// Framed as "Studien zufolge" and paired with `heroSource` wherever used, so
// it never reads as one of our own measurements — conflating the two would
// undermine the "wir schätzen nichts" promise this file otherwise keeps, and
// a specific number attributed to us without one would be exactly the kind
// of unsubstantiated advertising claim the UWG (Gesetz gegen den unlauteren
// Wettbewerb) targets.

// "5 Punkte gefunden · 1 kritisch · 2 hoch" — zero severities are left out, so
// nothing reads "0 kritisch". Shared by both languages via the word lists.
const findingsChip = (words, total, c, h, m, l) => {
  const parts = [words.found(total)];
  [[c, words.critical], [h, words.high], [m, words.medium], [l, words.low]].forEach(([n, w]) => {
    if (n > 0) parts.push(`${n} ${w}`);
  });
  return parts.join(" · ");
};

// Medians can be fractional: German writes 30,5, English 30.5. Integers are unchanged.
const numDe = (n) => String(n).replace(".", ",");

export const S = {
  de: {
    notMeasuredYet: "Die Messung für diesen Bericht ist noch nicht abgeschlossen.",
    scoreLabel: "von 100 Punkten digitale Sichtbarkeit",
    // `term` is the search we actually measured (maps_rank.keyword). Google's
    // per-business `category` is NOT used: it can be an odd pick (a Kfz
    // appraiser is filed under "Banken und Finanzen") and would put nonsense
    // in the sentence a prospect reads first.
    verdictLine: {
      critical: (city, term) =>
        term ? `Wer online nach „${term}“ sucht, findet Sie derzeit kaum.`
        : city ? `Wer in ${city} online nach Ihrem Angebot sucht, findet Sie derzeit kaum.`
        : "Wer online nach Ihrem Angebot sucht, findet Sie derzeit kaum.",
      weak: () => "Man findet Sie online — aber im Wesentlichen nur, wenn man Ihren Namen schon kennt.",
      mid: () => "Ihre Basis steht. Bei genau den Suchen, mit denen neue Kunden anfangen, liegen andere vor Ihnen.",
      strong: () => "Sie stehen gut da. Es fehlen die letzten Punkte, die darüber entscheiden, wer ganz oben steht.",
      top: () => "Sie gehören zu den sichtbarsten Betrieben in Ihrer Region.",
    },
    // Data-driven hero headlines (summaryModel.heroHeadline); "band" falls back to verdictLine.
    headline: {
      mapsTopGrid: (rank, visible, total) => `Bei Google Maps stehen Sie im Zentrum Ihrer Region auf Platz ${rank} — aber nur an ${visible} von ${total} Messpunkten rund um diesen Punkt erscheinen Sie überhaupt. Fast jede zweite lokale Suche endet bei einem der drei obersten Treffer — an den anderen Punkten sind das gerade nicht Sie.`,
      mapsTopGridAll: (rank, total) => `Bei Google Maps stehen Sie im Zentrum Ihrer Region auf Platz ${rank} und erscheinen an allen ${total} Messpunkten — bei den übrigen Prüfpunkten gibt es Lücken.`,
      mapsTop: (rank) => `Bei Google Maps stehen Sie auf Platz ${rank} — Betriebe in den Top 3 bekommen laut Studien deutlich mehr Anrufe und Anfragen als Platz 4 bis 10. Bei den übrigen Prüfpunkten gibt es trotzdem noch Lücken.`,
      mapsMid: (rank, total) => `Platz ${rank}${total ? ` von ${total}` : ""} klingt in Ordnung — ist es aber nicht: Betriebe in den Top 3 bei Google Maps bekommen laut Studien fast doppelt so viele Anrufe, Klicks und Wegbeschreibungs-Anfragen wie Betriebe auf Platz 4 bis 10. Diese Anfragen gehen heute an Ihre Konkurrenz.`,
      gridWeak: (missing, total, kw) => kw
        ? `In ${missing} von ${total} Stadtteilen rund um Sie sehen Kunden, die „${kw}" suchen, zuerst einen anderen Betrieb bei Google Maps — nicht Sie. Fast jede zweite lokale Suche endet bei einem der drei obersten Treffer.`
        : `In ${missing} von ${total} Stadtteilen rund um Sie sehen Kunden zuerst einen anderen Betrieb bei Google Maps — nicht Sie. Fast jede zweite lokale Suche endet bei einem der drei obersten Treffer.`,
    },
    // Small print under the hero headline, only when that headline just cited
    // the general local-search statistic above (never for the plain band
    // line or the "already visible everywhere" case, which cite nothing).
    heroSource: "Quelle: unabhängige Studien zum Nutzerverhalten im Google-Local-Pack.",
    // Replaces the absolute score-band badge only when it would otherwise
    // contradict the regional-median comparison right below it (see
    // aboveMedianButLowBand in SummaryReport.js).
    aboveMedianBadge: (gap) => gap > 0 ? `Über dem regionalen Median, ${gap} Punkte hinter den Top 3` : "Über dem regionalen Median",
    strengths: {
      title: "Was schon gut läuft",
      rating: (r, n) => `${numDe(r)} Sterne bei ${n} ${n === 1 ? "Bewertung" : "Bewertungen"}`,
      maps: (rank, total) => `Google Maps: Platz ${rank} von ${total}`,
      pillarFull: (label, score, max) => `${label}: voll erfüllt (${score}/${max})`,
      bridge: "Das Fundament ist da — was fehlt, sehen Sie unten.",
    },
    noSite: {
      lost: (n) => `Die fehlende Website kostet Sie ${n} von 100 Punkten.`,
      unmeasurable: "Ohne eigene Website nicht messbar",
      cardTitle: "Website neu aufbauen lassen",
      cardBody: "Ohne eigene Website fehlen die Punkte in den Bereichen, die von ihr abhängen. Fordern Sie ein Angebot für eine neue Website an.",
      cardCta: "Angebot für eine neue Website anfordern",
    },
    competitorFacts: {
      title: (label) => `Im Detail: Sie und ${label}`,
      ahead: (area, label, theirs, yours, max) => `Bei „${area}“ liegt ${label} vorn: ${theirs}/${max} gegenüber Ihren ${yours}/${max} Punkten.`,
      behind: (area, label, theirs, yours, max) => `Bei „${area}“ liegen Sie vor ${label}: ${yours}/${max} gegenüber ${theirs}/${max} Punkten.`,
    },
    keyTakeaways: {
      title: "Das Wichtigste in 30 Sekunden",
      issuesTitle: "Die wichtigsten Baustellen",
    },
    trust: {
      title: "Wer misst hier?",
      company: "Positivup ist eine Marke der Positivads Ltd, London (Companies House 14715482).",
      how: "Wir messen ausschließlich öffentlich erreichbare Daten: Google Maps, die Google-Suche, Ihre eigene Website und eine echte Anfrage an ChatGPT. Nichts davon ist geschätzt — jede Zahl in diesem Bericht können Sie selbst nachprüfen.",
      imprint: "Impressum",
      privacy: "Datenschutz",
    },
    benchmarkLine: (median, cohortSize) => (
      cohortSize
        ? <>Wir haben dafür <strong>{cohortSize} Betriebe</strong> Ihrer Branche in Ihrer Region gemessen. Der Median liegt bei <strong>{median}/100</strong>.</>
        : <>Vergleichbare Unternehmen in Ihrer Region liegen im Median bei <strong>{median}/100</strong>.</>
    ),
    // Short caption next to the ScoreBenchmarkBar marker — same "Region"/
    // "Median" wording as benchmarkLine above, just short enough to sit
    // beside a bar instead of inside a sentence.
    benchmarkCaption: "Regionaler Median",

    topFindingsTitle: "Drei Dinge, die uns sofort aufgefallen sind",
    topFindingsTitleFew: "Das ist uns sofort aufgefallen",
    topFindingsDesc: "Diese drei stehen hier offen. Dafür müssen Sie nichts kaufen.",
    selfCheckLabel: "Selbst prüfen:",
    hooks: {
      noWebsite: (city) => (
        <>Zu Ihrem Betrieb gibt es keine eigene Website. Alles, was ein Kunde{city ? ` in ${city}` : ""} über Sie findet, steht damit auf Seiten, die Ihnen nicht gehören.</>
      ),
      mapsRank: (rank, total, kw) => (
        <>Bei der Suche „{kw}“ stehen Sie bei Google Maps auf <strong>Platz {rank} von {total}</strong>. Die ersten drei Ergebnisse bekommen den größten Teil der Anrufe.</>
      ),
      mapsMissing: (total, kw) => (
        <>Bei der Suche „{kw}“ erscheinen Sie unter den ersten <strong>{total} Ergebnissen</strong> bei Google Maps nicht.</>
      ),
      aiBlocked: (bots) => (
        <>Ihre Datei <code>robots.txt</code> sperrt <strong>{bots}</strong> aus. Das sind genau die Programme, mit denen ChatGPT und Perplexity Websites lesen.</>
      ),
      citations: (found, total, missing) => (
        <>Sie sind in <strong>{found} von {total}</strong> geprüften Branchenverzeichnissen und sozialen Netzwerken auffindbar. <strong>{missing}</strong> fehlen.</>
      ),
      nap: (diff, checked) => (
        <>Auf <strong>{diff} von {checked}</strong> geprüften Portalen weichen Ihre Adresse oder Telefonnummer von Ihrem Google-Profil ab. Google verlässt sich dann weniger auf Ihre Standortdaten.</>
      ),
      reviewRecency: (days) => (
        <>Ihre neueste Google-Bewertung ist <strong>{days} Tage</strong> alt. Google bewertet auch, wie frisch Bewertungen sind — nicht nur, wie viele es sind.</>
      ),
    },
    hookChecks: {
      noWebsite: () => "Suchen Sie Ihren Firmennamen bei Google — und schauen Sie, wem die Treffer gehören.",
      mapsRank: (kw) => `Suchen Sie „${kw}“ bei Google Maps und zählen Sie nach.`,
      mapsMissing: (kw) => `Suchen Sie „${kw}“ bei Google Maps und schauen Sie, wer angezeigt wird.`,
      aiBlocked: (domain) => `Rufen Sie ${domain}/robots.txt im Browser auf — die Sperren stehen dort im Klartext.`,
      citations: () => "Googeln Sie Ihren Firmennamen — Sie sehen, welche Portale Sie zeigen und welche nicht.",
      nap: () => "Bitte prüfen: ein abweichender Eintrag ist oft ein alter Eintrag, kein Fehler von Ihnen.",
      reviewRecency: () => "Öffnen Sie Ihr Google-Profil und schauen Sie auf das Datum der obersten Bewertung.",
    },

    compareTitle: "Sie und die drei sichtbarsten Betriebe in Ihrer Region",
    compareDesc: "Wer bei Google Maps und in KI-Antworten oben steht, bekommt Anfragen, ohne dafür zu werben. Wer weiter unten steht, wird oft gar nicht erst angesehen. Die Namen unten sind echte Betriebe aus Ihrer Umgebung — Sie können jeden davon selbst nachschlagen.",
    // The scoreboard above already states the exact numbers (your score, the
    // gap, the leader's score) — this line's job is the "so what", not a
    // third restatement of the same figure (owner-relayed review pattern,
    // applied here 2026-09-28: counter "this sounds like a lot of work"
    // and point at the concrete next step instead of just naming the gap).
    compareGap: (target, gap) => (
      <>Diese <strong>{gap} Punkte</strong> bis auf das Niveau der sichtbarsten Betriebe ({target}/100) sind kein Website-Umbau — einzelne, konkrete Korrekturen genügen. Wo genau sie ansetzen, steht im ausführlichen Bericht.</>
    ),

    // Lead sentence of the yellow legal card, stating the breadth of the check
    // in plain terms — no specific legal titles, no claim of a violation. The
    // count-of-flagged-items sentence and the "keine Rechtsberatung" disclaimer
    // that follow it come from i18n.js (shared with the paid tab) and stay as is.
    legalIntro: "Wir prüfen über 15 rechtliche Aspekte Ihrer Website: Impressum, Datenschutzerklärung, Cookie-Einwilligung, eingebundene Drittanbieter und mehr.",
    // Free-report-only title for the yellow legal card — sharper than the
    // shared L.legalTitle ("Rechtliche Prüfpunkte") used in the paid tab's own
    // legal detail, where the sales framing no longer fits (owner-relayed
    // review, 2026-09-28).
    legalCardTitle: (n) => n === 1
      ? "1 rechtlicher Punkt, den Sie kennen sollten, bevor es ein Anwalt tut"
      : `${n} rechtliche Punkte, die Sie kennen sollten, bevor es ein Anwalt tut`,

    aiTest: {
      title: "Wir haben ChatGPT gefragt",
      // Shown instead of the neutral title once the real answer confirms it —
      // only when we actually found named competitors in the answer text
      // (never a guessed count) (owner-relayed review, 2026-09-28).
      titleNotMentioned: (n) => n === 1
        ? "ChatGPT empfiehlt Ihren Kunden heute einen anderen Betrieb. Ihren nicht."
        : `ChatGPT empfiehlt Ihren Kunden heute ${n} andere Betriebe. Ihren nicht.`,
      button: "Antwort jetzt abrufen",
      running: (q) => `Wir fragen gerade ChatGPT: „${q}“ — das dauert etwa 20 Sekunden.`,
      mentioned: "In dieser Antwort genannt.",
      notMentioned: "In dieser Antwort nicht genannt.",
      namedInstead: (names) => `Genannt werden stattdessen: ${names}.`,
      answerHeading: "Was ChatGPT geantwortet hat",
      // Rewritten for clarity (owner feedback, 2026-09-30): the raw ChatGPT
      // answer above can run long, and the old note ("Eine einzelne, echte
      // Antwort, keine Statistik...") didn't clearly say what the paid report
      // actually adds — state the concrete deliverable instead.
      note: "Das ist nur eine von 12 echten Testfragen. Im ausführlichen Bericht sehen Sie bei jeder einzelnen, ob Sie genannt werden — oder wer stattdessen genannt wird.",
      failed: "Der Anbieter hat gerade nicht geantwortet. Das ist ein technisches Problem auf dessen Seite — kein Ergebnis, und kein Hinweis auf Ihre Sichtbarkeit. Bitte später erneut versuchen.",
      busy: "Der KI-Test ist im Moment sehr gefragt und heute nicht mehr verfügbar. Das ist kein Ergebnis und kein Hinweis auf Ihre Sichtbarkeit. Bitte morgen erneut versuchen.",
      unavailable: "Für diesen Bericht ist kein KI-Test verfügbar.",
    },

    offer: {
      title: "Der ausführliche Bericht",
      priceNote: "einmalig · kein Abo",
      lead: (n) => `Alle ${n} gefundenen Punkte, jeweils mit Beweis und dem konkreten Schritt, der ihn behebt — plus die Messungen, die wir für den kostenlosen Bericht nicht durchführen.`,
      includes: [
        "Jeden einzelnen gefundenen Punkt mit Beweis, Begründung und dem konkreten Schritt zur Behebung",
        "Den echten ChatGPT-Test: 12 Fragen, die Ihre Kunden tatsächlich stellen — mit den Antworten im Wortlaut und den Namen, die statt Ihnen genannt werden",
        "Google-KI-Übersicht und Google-KI-Modus: erscheinen Sie dort, oder nicht",
        "Die Verzeichnisse, in denen Sie fehlen — namentlich, mit Anmeldelink",
        "Jede Plattform, auf der Ihre Adresse oder Telefonnummer abweicht",
        "Ladezeit, Core Web Vitals und verweisende Domains Ihrer Website — mit echten Messwerten, nicht mit Schätzungen",
        "Ihre Bewertungen im Detail: Aktualität, Antwortrate, Beschwerdethemen, und welche Bewertungen Sie bei Google melden könnten",
        "Die rechtlichen Prüfpunkte im Klartext, inklusive der betroffenen Bilder (Hinweis, keine Rechtsberatung)",
      ],
      delivery: (hours) => `Nach der Zahlung starten die Messungen sofort. Ich sehe den fertigen Bericht persönlich durch, bevor Sie ihn bekommen — spätestens ${hours} Stunden nach Zahlungseingang liegt der Link in Ihrem Postfach. Meist geht es schneller.`,
      noCall: "Kein Termin, kein Telefonat, kein Verkaufsgespräch nötig. Sie kaufen ein Dokument, nicht eine Beratung.",
      // Shown only while free consulting slots are left (real number from the API).
      consultingFree: "Beratungsmonat per Ticket für die ersten {total} Kunden inklusive: Noch {n} von {total} Plätzen.",
      // Used instead of the line above for as long as free_slots_left equals
      // free_slots_total — "Noch 100 von 100 Plätzen" reads as "nobody has
      // bought this yet" (reverse social proof), so the countdown only shows
      // once it's actually true (owner-relayed review, 2026-09-28).
      consultingFreeAll: "Beratungsmonat per Ticket für die ersten {total} Kunden inklusive.",
      // Hidden by OfferBlock until SALES.paymentLive / SALES.guaranteeLive.
      payNote: "Zahlung per Karte, PayPal oder SEPA-Lastschrift. Die Rechnung mit ausgewiesener MwSt. kommt automatisch per E-Mail.",
      guarantee: (price) => `Wenn der Bericht einen Punkt nennt, der bei Ihnen nachweislich nicht zutrifft, erstatte ich die ${price} vollständig. Eine kurze E-Mail genügt, keine Begründung nötig.`,
      ctaRequest: (price) => `Vollständigen Bericht für ${price} anfordern`,
      ctaPay: (price) => `Vollständigen Bericht für ${price} freischalten`,
      ctaSticky: (price) => `Ausführlicher Bericht · ${price}`,
      ctaStickyLabel: "Zum Angebot für den ausführlichen Bericht springen",
      // Shown instead of payment wording while payment is not live: says what
      // actually happens when the button is pressed, and nothing more.
      requestNote: "Sie hinterlassen Ihre Kontaktdaten — ich melde mich per E-Mail oder, wenn Sie das möchten, telefonisch mit dem weiteren Ablauf.",
      afterTitle: "Und danach?",
      afterDesc: "Der Bericht endet mit drei Wegen: selbst umsetzen (die Liste ist dafür gemacht), an Ihre Agentur oder Ihren Webbetreuer weitergeben, oder ein Angebot von uns anfordern. Welchen Sie nehmen, entscheiden Sie — der Bericht ist in allen drei Fällen derselbe.",
    },

    faqTitle: "Häufige Fragen",
    faq: (price, hours) => [
      { q: "Brauche ich technisches Wissen, um damit etwas anfangen zu können?",
        a: "Nein. Jeder Punkt steht in einem Satz Alltagssprache, dazu der konkrete Schritt. Sie können den Bericht genauso gut Ihrer Agentur oder Ihrem Webbetreuer geben — er ist so geschrieben, dass er dort direkt abarbeitbar ist." },
      { q: "Woher kommen die Daten?",
        a: "Aus Ihrer öffentlich erreichbaren Website, Ihrem Google-Unternehmensprofil, Google Maps und der Google-Suche — und aus echten Anfragen an ChatGPT und andere KI-Assistenten. Alles öffentlich zugänglich. Wir haben keinen Zugang zu Ihren internen Daten und brauchen keinen." },
      { q: "Wie schnell bekomme ich den Bericht?",
        a: `Die Messungen starten direkt nach der Zahlung. Weil ich jeden Bericht persönlich durchsehe, bevor er rausgeht, nennen wir ${hours} Stunden — in der Regel ist es schneller.` },
      { q: "Ist das eine Rechtsberatung?",
        a: "Nein. Wir weisen nur darauf hin, was auf Ihrer Website technisch sichtbar ist oder fehlt. Ob und in welchem Umfang eine Pflicht für Sie gilt, hängt von Ihrem Einzelfall ab — bitte lassen Sie diese Punkte im Zweifel anwaltlich prüfen." },
      { q: "Rufen Sie mich danach an?",
        a: "Nur wenn Sie darum bitten. Nach dem Kauf bekommen Sie den Bericht, sonst nichts." },
      { q: "Wie lange sind die Ergebnisse gültig?",
        a: "Jede Messung trägt ihr Datum. Rankings und KI-Antworten ändern sich laufend. Die Lücken auf Ihrer Website dagegen bleiben, bis sie behoben sind — deshalb steht bei jedem Punkt der konkrete Schritt." },
      { q: "Was, wenn der Bericht nichts für mich hergibt?", requires: "guarantee",
        a: `Wenn er einen Punkt nennt, der bei Ihnen nachweislich nicht zutrifft, bekommen Sie die ${price} zurück. Eine kurze E-Mail genügt.` },
    ],

    glance: {
      title: "Ergebnis auf einen Blick",
      primaryCta: "Alle Punkte ansehen",
      chips: {
        findings: (total, c, h, m, l) => findingsChip({
          found: (n) => `${n} ${n === 1 ? "Punkt" : "Punkte"} gefunden`,
          critical: "kritisch", high: "hoch", medium: "mittel", low: "niedrig",
        }, total, c, h, m, l),
        legal: (n) => `${n} ${n === 1 ? "rechtlicher Punkt" : "rechtliche Punkte"}`,
        gap: (gap, label) => `${gap} ${gap === 1 ? "Punkt" : "Punkte"} hinter ${label}`,
        maps: (rank, total) => `Google Maps: Platz ${rank} von ${total}`,
      },
    },

    pillars: {
      title: "Ihre 6 Bereiche im Überblick",
      desc: "Wo Sie stark sind, und wo noch Punkte liegen. Was genau in jedem Bereich fehlt, steht im ausführlichen Bericht.",
      locked: (n) => (n === 1 ? "1 Punkt im Bericht" : `${n} Punkte im Bericht`),
      complete: "Alles bestanden",
      completeHint: "Details im ausführlichen Bericht",
      sampleOpen: () => "1 Punkt offen — unten aufgeschlüsselt",
      // Sits right under a real score (e.g. "4/20") that already reflects the
      // automated checks — without saying so explicitly, "0 von 24 Punkten
      // manuell geprüft" reads as "you measured nothing" instead of "there's
      // a manual layer on top, not yet done" (owner-relayed review, 2026-09-28).
      manualReviewed: (reviewed, total) =>
        `Der Wert oben zählt die automatischen Prüfungen — ${reviewed} von ${total} weiteren Punkten sind bislang manuell geprüft.`,
      radarYou: "Sie",
      radarTitle: "Ihre Punkte je Bereich",
      radarBenchmark: "Ø der 3 sichtbarsten Betriebe",
      radarBenchmarkNote: "Gestrichelt: Durchschnitt der drei sichtbarsten Betriebe — nicht der Regionswert oben.",
      radarLabels: { web: "Website", onpage: "On-Page-SEO", local: "Local SEO", gbp: "Google-Profil", reviews: "Bewertungen", ai: "KI-Sichtbarkeit" },
      compareByArea: "Bereich für Bereich vergleichen",
    },

    tiles: {
      title: "Was Sie im ausführlichen Bericht erwartet",
      desc: "Diese Messungen haben wir für Sie durchgeführt. Die Ergebnisse im Detail — mit Namen, Beweisen und Lösungsschritten — sehen Sie im Bericht.",
      lockedHint: "Im ausführlichen Bericht",
      // `found`/`diff` etc. are null when this business's measurement is
      // missing, errored, or (for nap) genuinely fully consistent already —
      // the tile still always renders (owner correction 2026-10-01: "veri
      // yok olsada bunlar her raporda olmalı"), just with a generic
      // description of the check instead of a number we don't actually have.
      directories: (found, total, missing) => ({
        title: "Branchenverzeichnisse",
        body: found == null
          ? "Wir prüfen, in welchen der wichtigsten Branchenverzeichnisse Sie eingetragen sind — und wo noch Einträge fehlen."
          : missing <= 0 ? `${found} von ${total} gefunden.`
          : missing === 1 ? `${found} von ${total} gefunden — 1 fehlt. Welches, steht im Bericht.`
          : `${found} von ${total} gefunden — ${missing} fehlen. Welche, steht im Bericht.`,
      }),
      nap: (diff, checked) => ({
        title: "Adresse & Telefon",
        body: diff == null
          ? "Wir prüfen, ob Name, Adresse und Telefonnummer über alle Plattformen hinweg übereinstimmen."
          : diff === 0 ? `Auf allen ${checked} geprüften Plattformen stimmen Ihre Angaben überein.`
          : diff === 1 ? `Auf 1 von ${checked} ${checked === 1 ? "Plattform" : "Plattformen"} weicht Ihre Angabe ab. Welche, steht im Bericht.`
          : `Auf ${diff} von ${checked} Plattformen weichen Ihre Angaben ab. Welche, steht im Bericht.`,
      }),
      ai: () => ({
        title: "Der echte ChatGPT-Test",
        body: "12 Fragen, die Ihre Kunden stellen — mit den Antworten im Wortlaut und den Namen, die statt Ihnen genannt werden.",
      }),
      gbp: () => ({
        title: "Google-Unternehmensprofil-Tiefenprüfung",
        body: "Über 30 Prüfpunkte an Ihrem Google-Profil — von Kategorie bis Fotos.",
      }),
      speed: (hasWebsite) => ({
        title: "Ladezeit & Core Web Vitals",
        body: hasWebsite === false
          ? "Sobald Sie eine Website haben, prüfen wir Ladezeit und Core Web Vitals mit echten Messwerten."
          : "Mit echten Messwerten, nicht mit Schätzungen.",
      }),
      legal: (hasWebsite) => ({
        title: "Rechtliche Prüfung Ihrer Website",
        body: hasWebsite === false
          ? "Sobald Sie eine Website haben, prüfen wir sie auf rechtliche Vorgaben und mögliche Foto-Lizenzrisiken (keine Rechtsberatung)."
          : "Wir prüfen, ob Ihre Website die rechtlichen Vorgaben einhält, und sehen uns Ihre eingesetzten Fotos auf mögliche Lizenz-/Urheberrechtsrisiken an — beides mit Befund im Bericht (keine Rechtsberatung).",
      }),
      reviews: (count) => ({
        title: "Ihre Bewertungen im Detail",
        body: !count
          ? "Noch keine Bewertungen vorhanden — im Bericht zeigen wir Ihnen, wie Sie einen Bewertungsprozess aufbauen."
          : `${count} ${count === 1 ? "Bewertung" : "Bewertungen"}: Aktualität, Antwortrate, Beschwerdethemen — und welche Sie bei Google melden könnten.`,
      }),
      keywords: () => ({
        title: "Ihre wichtigsten Suchbegriffe",
        body: "Bis zu 5 Suchbegriffe mit echtem Suchvolumen, Trend und Ihrer Position.",
      }),
    },

    // Fill-ins for "Drei Dinge" when fewer than three real hooks exist.
    // weakestPillar: the first argument is the already-translated pillar label;
    // the caller maps the model's key through T[lang].pillars.
    fallbackHooks: {
      weakestPillar: (label, score, max) => (
        <>Ihr schwächster Bereich ist „{label}“: <strong>{score} von {max} Punkten</strong>.</>
      ),
      strongestPillar: (label, score, max) => (
        <>Ihre Stärke ist „{label}“: <strong>{score} von {max} Punkten</strong>.</>
      ),
      reviewsVsMedian: (count, median) => (
        <>Sie haben <strong>{count} Google-{count === 1 ? "Bewertung" : "Bewertungen"}</strong> — der Median Ihrer Region liegt bei <strong>{numDe(median)}</strong>.</>
      ),
    },

    reviewsSummary: {
      title: "Ihre Google-Bewertungen im Überblick",
      ratingLabel: "Durchschnitt",
      countLabel: "Bewertungen",
      reviewsMore: "Einordnung zu Ihren Bewertungen",
    },

    testimonials: {
      title: "Das sagen unsere Kunden",
    },

    sender: {
      title: "Wer das gemessen hat",
      desc: (name) => `${name} — ich messe die digitale Sichtbarkeit lokaler Betriebe und schreibe die Berichte selbst. Fragen zu einer einzelnen Zahl in diesem Bericht beantworte ich gern direkt.`,
      imprint: "Impressum",
      terms: "AGB",
      privacy: "Datenschutz",
    },
  },

  en: {
    notMeasuredYet: "The measurement for this report is not complete yet.",
    scoreLabel: "out of 100 points digital visibility",
    verdictLine: {
      critical: (city, term) =>
        term ? `Anyone searching online for “${term}” will hardly find you right now.`
        : city ? `Anyone in ${city} searching online for what you offer will hardly find you right now.`
        : "Anyone searching online for what you offer will hardly find you right now.",
      weak: () => "People can find you online — but essentially only if they already know your name.",
      mid: () => "Your foundation is in place. For exactly the searches new customers start with, others rank ahead of you.",
      strong: () => "You are doing well. What is missing are the last points that decide who ends up on top.",
      top: () => "You are among the most visible businesses in your region.",
    },
    headline: {
      mapsTopGrid: (rank, visible, total) => `On Google Maps you rank #${rank} at the centre of your region — but you only appear at ${visible} of ${total} measuring points around it. Nearly one in two local searches ends on one of the top three results — at the other points, that isn't you.`,
      mapsTopGridAll: (rank, total) => `On Google Maps you rank #${rank} at the centre of your region and appear at all ${total} measuring points — the other checks show gaps.`,
      mapsTop: (rank) => `On Google Maps you rank #${rank} — studies show top-3 listings get noticeably more calls and enquiries than positions 4 to 10. The other checks still show gaps.`,
      mapsMid: (rank, total) => `Rank #${rank}${total ? ` of ${total}` : ""} sounds fine — it isn't: studies show Google Maps top-3 listings get almost twice as many calls, clicks and direction requests as positions 4 to 10. Those enquiries are going to your competitors right now.`,
      gridWeak: (missing, total, kw) => kw
        ? `In ${missing} of ${total} areas around you, customers searching for "${kw}" see a different business first on Google Maps — not you. Nearly one in two local searches ends on one of the top three results.`
        : `In ${missing} of ${total} areas around you, customers see a different business first on Google Maps — not you. Nearly one in two local searches ends on one of the top three results.`,
    },
    // Small print under the hero headline, only when it just cited the
    // general local-search statistic above.
    heroSource: "Source: independent studies on user behaviour in Google's local pack.",
    aboveMedianBadge: (gap) => gap > 0 ? `Above the regional median, ${gap} points behind the top 3` : "Above the regional median",
    strengths: {
      title: "What already works",
      rating: (r, n) => `${r} stars from ${n} ${n === 1 ? "review" : "reviews"}`,
      maps: (rank, total) => `Google Maps: rank ${rank} of ${total}`,
      pillarFull: (label, score, max) => `${label}: fully met (${score}/${max})`,
      bridge: "The foundation is there — what is missing, you see below.",
    },
    noSite: {
      lost: (n) => `The missing website costs you ${n} of 100 points.`,
      unmeasurable: "Not measurable without a website of your own",
      cardTitle: "Have a new website built",
      cardBody: "Without a website of your own, the areas that depend on it score nothing. Request a quote for a new website.",
      cardCta: "Request a quote for a new website",
    },
    competitorFacts: {
      title: (label) => `In detail: you and ${label}`,
      ahead: (area, label, theirs, yours, max) => `In “${area}” ${label} is ahead: ${theirs}/${max} against your ${yours}/${max} points.`,
      behind: (area, label, theirs, yours, max) => `In “${area}” you are ahead of ${label}: ${yours}/${max} against ${theirs}/${max} points.`,
    },
    keyTakeaways: {
      title: "The essentials in 30 seconds",
      issuesTitle: "The main problem areas",
    },
    trust: {
      title: "Who is measuring here?",
      company: "Positivup is a brand of Positivads Ltd, London (Companies House 14715482).",
      how: "We only measure publicly accessible data: Google Maps, Google Search, your own website and a real query to ChatGPT. Nothing is estimated — you can check every number in this report yourself.",
      imprint: "Imprint",
      privacy: "Privacy",
    },
    benchmarkLine: (median, cohortSize) => (
      cohortSize
        ? <>We measured <strong>{cohortSize} businesses</strong> in your sector and region for this. The median is <strong>{median}/100</strong>.</>
        : <>Comparable businesses in your region have a median of <strong>{median}/100</strong>.</>
    ),
    benchmarkCaption: "Regional median",

    topFindingsTitle: "Three things that stood out immediately",
    topFindingsTitleFew: "What stood out immediately",
    topFindingsDesc: "These three are shown in full. You do not have to buy anything for them.",
    selfCheckLabel: "Check it yourself:",
    hooks: {
      noWebsite: (city) => (
        <>There is no website of your own for your business. Everything a customer{city ? ` in ${city}` : ""} finds about you therefore sits on pages you do not own.</>
      ),
      mapsRank: (rank, total, kw) => (
        <>For the search “{kw}” you rank <strong>#{rank} of {total}</strong> on Google Maps. The top three results get most of the calls.</>
      ),
      mapsMissing: (total, kw) => (
        <>For the search “{kw}” you do not appear among the first <strong>{total} results</strong> on Google Maps.</>
      ),
      aiBlocked: (bots) => (
        <>Your <code>robots.txt</code> file blocks <strong>{bots}</strong>. Those are exactly the programs ChatGPT and Perplexity use to read websites.</>
      ),
      citations: (found, total, missing) => (
        <>You can be found in <strong>{found} of {total}</strong> checked business directories and social networks. <strong>{missing}</strong> are missing.</>
      ),
      nap: (diff, checked) => (
        <>On <strong>{diff} of {checked}</strong> checked portals your address or phone number differs from your Google profile. Google then relies less on your location data.</>
      ),
      reviewRecency: (days) => (
        <>Your newest Google review is <strong>{days} days</strong> old. Google also weighs how fresh reviews are — not just how many there are.</>
      ),
    },
    hookChecks: {
      noWebsite: () => "Search your business name on Google — and see who owns the results.",
      mapsRank: (kw) => `Search “${kw}” on Google Maps and count for yourself.`,
      mapsMissing: (kw) => `Search “${kw}” on Google Maps and see who is shown.`,
      aiBlocked: (domain) => `Open ${domain}/robots.txt in your browser — the blocks are written there in plain text.`,
      citations: () => "Google your business name — you will see which portals show you and which do not.",
      nap: () => "Please check: a differing entry is often an old entry, not a mistake on your side.",
      reviewRecency: () => "Open your Google profile and look at the date of the top review.",
    },

    compareTitle: "You and the three most visible businesses in your region",
    compareDesc: "Whoever ranks on top on Google Maps and in AI answers gets enquiries without advertising. Whoever ranks lower is often not even looked at. The names below are real businesses from your area — you can look up each one yourself.",
    compareGap: (target, gap) => (
      <>Closing these <strong>{gap} points</strong> to the level of the most visible businesses ({target}/100) doesn't mean rebuilding your website — individual, concrete fixes are enough. Exactly where they apply is in the detailed report.</>
    ),

    legalIntro: "We check over 15 legal aspects of your website: imprint, privacy policy, cookie consent, embedded third-party services and more.",
    legalCardTitle: (n) => n === 1
      ? "1 legal point you should know about before a lawyer does"
      : `${n} legal points you should know about before a lawyer does`,

    aiTest: {
      title: "We asked ChatGPT",
      titleNotMentioned: (n) => n === 1
        ? "ChatGPT is recommending your customers one other business today. Not yours."
        : `ChatGPT is recommending your customers ${n} other businesses today. Not yours.`,
      button: "Get the answer now",
      running: (q) => `We are asking ChatGPT right now: “${q}” — this takes about 20 seconds.`,
      mentioned: "Named in this answer.",
      notMentioned: "Not named in this answer.",
      namedInstead: (names) => `Named instead: ${names}.`,
      answerHeading: "What ChatGPT answered",
      note: "This is just one of 12 real test questions. The detailed report shows you, for each one, whether you're mentioned — or who is mentioned instead.",
      failed: "The provider did not answer just now. That is a technical problem on their side — not a result, and not a statement about your visibility. Please try again later.",
      busy: "The AI test is in high demand and no longer available today. That is not a result and not a statement about your visibility. Please try again tomorrow.",
      unavailable: "No AI test is available for this report.",
    },

    offer: {
      title: "The detailed report",
      priceNote: "one-time · no subscription",
      lead: (n) => `All ${n} findings, each with proof and the concrete step that fixes it — plus the measurements we do not run for the free report.`,
      includes: [
        "Every single finding with proof, reasoning and the concrete step that fixes it",
        "The real ChatGPT test: 12 questions your customers actually ask — with the answers verbatim and the names mentioned instead of you",
        "Google AI Overview and Google AI Mode: do you appear there, or not",
        "The directories where you are missing — by name, with a sign-up link",
        "Every platform where your address or phone number differs",
        "Load time, Core Web Vitals and referring domains of your website — real measurements, not estimates",
        "Your reviews in detail: recency, response rate, complaint themes, and which reviews you could report to Google",
        "The legal check points in plain language, including the affected images (a hint, not legal advice)",
      ],
      delivery: (hours) => `Measurements start right after payment. I review the finished report personally before you receive it — the link is in your inbox at the latest ${hours} hours after payment. Usually faster.`,
      noCall: "No appointment, no phone call, no sales pitch needed. You buy a document, not a consultation.",
      consultingFree: "Consulting month by ticket included for the first {total} customers: {n} of {total} places left.",
      consultingFreeAll: "Consulting month by ticket included for the first {total} customers.",
      payNote: "Payment by card, PayPal or SEPA direct debit. The invoice with VAT shown follows automatically by e-mail.",
      guarantee: (price) => `If the report names a point that demonstrably does not apply to you, I refund the full ${price}. A short e-mail is enough, no reason needed.`,
      ctaRequest: (price) => `Request the full report for ${price}`,
      ctaPay: (price) => `Unlock the full report for ${price}`,
      ctaSticky: (price) => `Detailed report · ${price}`,
      ctaStickyLabel: "Jump to the offer for the detailed report",
      requestNote: "You leave your contact details — I will get back to you by e-mail or, if you prefer, by phone with the next steps.",
      afterTitle: "And afterwards?",
      afterDesc: "The report ends with three routes: implement it yourself (the list is made for that), hand it to your agency or web contact, or request a quote from us. You decide which — the report is the same in all three cases.",
    },

    faqTitle: "Frequently asked questions",
    faq: (price, hours) => [
      { q: "Do I need technical knowledge to make use of it?",
        a: "No. Every point is written in one sentence of everyday language, plus the concrete step. You can just as well hand the report to your agency or web contact — it is written so they can work through it directly." },
      { q: "Where does the data come from?",
        a: "From your publicly reachable website, your Google Business Profile, Google Maps and Google Search — and from real queries to ChatGPT and other AI assistants. All publicly accessible. We have no access to your internal data and need none." },
      { q: "How quickly do I get the report?",
        a: `Measurements start right after payment. Because I review every report personally before it goes out, we say ${hours} hours — usually it is faster.` },
      { q: "Is this legal advice?",
        a: "No. We only point out what is technically visible or missing on your website. Whether and to what extent an obligation applies to you depends on your individual case — please have these points checked by a lawyer if in doubt." },
      { q: "Will you call me afterwards?",
        a: "Only if you ask. After purchase you get the report, nothing else." },
      { q: "How long are the results valid?",
        a: "Every measurement carries its date. Rankings and AI answers change constantly. The gaps on your website, however, stay until they are fixed — that is why every point comes with its concrete step." },
      { q: "What if the report has nothing for me?", requires: "guarantee",
        a: `If it names a point that demonstrably does not apply to you, you get the ${price} back. A short e-mail is enough.` },
    ],

    glance: {
      title: "Result at a glance",
      primaryCta: "See all points",
      chips: {
        findings: (total, c, h, m, l) => findingsChip({
          found: (n) => `${n} ${n === 1 ? "point" : "points"} found`,
          critical: "critical", high: "high", medium: "medium", low: "low",
        }, total, c, h, m, l),
        legal: (n) => `${n} legal ${n === 1 ? "point" : "points"}`,
        gap: (gap, label) => `${gap} ${gap === 1 ? "point" : "points"} behind ${label}`,
        maps: (rank, total) => `Google Maps: rank ${rank} of ${total}`,
      },
    },

    pillars: {
      title: "Your 6 areas at a glance",
      desc: "Where you are strong, and where points are still open. What exactly is missing in each area is in the detailed report.",
      locked: (n) => (n === 1 ? "1 point in the report" : `${n} points in the report`),
      complete: "All passed",
      completeHint: "Details in the detailed report",
      sampleOpen: () => "1 point open — broken down below",
      manualReviewed: (reviewed, total) =>
        `The score above already counts the automated checks — ${reviewed} of ${total} further points have been manually reviewed so far.`,
      radarYou: "You",
      radarTitle: "Your points per area",
      radarBenchmark: "Avg. of the 3 most visible businesses",
      radarBenchmarkNote: "Dashed: average of the three most visible businesses — not the regional value above.",
      radarLabels: { web: "Website", onpage: "On-Page SEO", local: "Local SEO", gbp: "Google Profile", reviews: "Reviews", ai: "AI Visibility" },
      compareByArea: "Compare area by area",
    },

    tiles: {
      title: "What awaits you in the detailed report",
      desc: "We have run these measurements for you. You see the results in detail — with names, proof and steps to fix them — in the report.",
      lockedHint: "In the detailed report",
      directories: (found, total, missing) => ({
        title: "Business directories",
        body: found == null
          ? "We check which of the major business directories list you — and which ones are still missing."
          : missing <= 0 ? `${found} of ${total} found.`
          : missing === 1 ? `${found} of ${total} found — 1 is missing. Which one is in the report.`
          : `${found} of ${total} found — ${missing} are missing. Which ones is in the report.`,
      }),
      nap: (diff, checked) => ({
        title: "Address & phone",
        body: diff == null
          ? "We check whether your name, address and phone number match across every platform."
          : diff === 0 ? `All ${checked} checked platforms match.`
          : diff === 1 ? `On 1 of ${checked} ${checked === 1 ? "platform" : "platforms"} your details differ. Which one is in the report.`
          : `On ${diff} of ${checked} platforms your details differ. Which ones is in the report.`,
      }),
      ai: () => ({
        title: "The real ChatGPT test",
        body: "12 questions your customers ask — with the answers verbatim and the names mentioned instead of you.",
      }),
      gbp: () => ({
        title: "Google Business Profile deep review",
        body: "Over 30 checkpoints on your Google profile — from category to photos.",
      }),
      speed: (hasWebsite) => ({
        title: "Load time & Core Web Vitals",
        body: hasWebsite === false
          ? "Once you have a website, we'll measure load time and Core Web Vitals with real data."
          : "With real measurements, not estimates.",
      }),
      legal: (hasWebsite) => ({
        title: "Legal review of your website",
        body: hasWebsite === false
          ? "Once you have a website, we'll check it for legal requirements and possible photo license risk (not legal advice)."
          : "We check whether your website meets the legal requirements, and review the photos you use for possible license/copyright risk — both with findings in the report (not legal advice).",
      }),
      reviews: (count) => ({
        title: "Your reviews in detail",
        body: !count
          ? "No reviews yet — the report shows you how to set up a review-collection process."
          : `${count} ${count === 1 ? "review" : "reviews"}: recency, response rate, complaint themes — and which ones you could report to Google.`,
      }),
      keywords: () => ({
        title: "Your key search terms",
        body: "Up to 5 search terms with real search volume, trend and your ranking position.",
      }),
    },

    fallbackHooks: {
      weakestPillar: (label, score, max) => (
        <>Your weakest area is “{label}”: <strong>{score} of {max} points</strong>.</>
      ),
      strongestPillar: (label, score, max) => (
        <>Your strength is “{label}”: <strong>{score} of {max} points</strong>.</>
      ),
      reviewsVsMedian: (count, median) => (
        <>You have <strong>{count} Google {count === 1 ? "review" : "reviews"}</strong> — the median in your region is <strong>{median}</strong>.</>
      ),
    },

    reviewsSummary: {
      title: "Your Google reviews at a glance",
      ratingLabel: "Average",
      countLabel: "Reviews",
      reviewsMore: "How your reviews compare",
    },

    testimonials: {
      title: "What our customers say",
    },

    sender: {
      title: "Who measured this",
      desc: (name) => `${name} — I measure the digital visibility of local businesses and write the reports myself. I am glad to answer questions about any single number in this report directly.`,
      imprint: "Imprint",
      terms: "Terms",
      privacy: "Privacy",
    },
  },
};
